import { useMemo, useState } from 'react';
import type { Celda, PreguntaResultado } from '@copathy/shared';
import { MOMENTO } from '@copathy/shared';
import { Contenedor } from '../componentes/Contenedor';
import { Combo } from '../componentes/Combo';
import { Boton } from '../componentes/Boton';
import { Grafico } from '../componentes/iconos';
import { useMigas } from '../lib/migas';
import { usePuede } from '../lib/auth';
import {
  useAplicacionesConResultados,
  useCortes,
  useResultados,
  useTextos,
} from '../lib/resultados';

/**
 * M8 — Resultados agregados.
 *
 * ── Las tres cosas que esta pantalla NO puede hacer mal ──
 *
 * 1. **Confundir «pocas respuestas» con «nadie respondió».** Son estados
 *    distintos y se dibujan distinto. Si se pintaran igual, alguien concluiría
 *    que su gente no participó cuando en realidad sí lo hizo y el mínimo tapa el
 *    detalle. Es la confusión más fácil de cometer y la más difícil de detectar.
 *
 * 2. **Mostrar el informe de un corte con el nombre de otro.** Al cambiar de área
 *    hay un instante donde los datos son los viejos; sin cuidado, la pantalla
 *    dice «Operaciones» sobre los números de «Ventas». Quien mira no tiene manera
 *    de notarlo. Por eso el corte va en la `queryKey` y mientras recarga no se
 *    dibujan números.
 *
 * 3. **Traer los textos libres sin que nadie los pida.** Van tras un clic, no con
 *    el informe (ver `useTextos`).
 *
 * ── Por qué la misma pantalla para COPATHY y para el cliente ──
 * Lo que cambia entre los dos no es la pantalla: es **qué devuelve el servidor**.
 * Copathy ve todas las aplicaciones y no la ata el mínimo; el cliente ve las
 * suyas y por debajo del mínimo recibe `entregable: false`. Dos pantallas
 * paralelas serían dos sitios donde arreglar el mismo detalle, y la que menos se
 * mire —la del cliente— es justo la que sostiene la frontera 3.
 */
export function Resultados() {
  useMigas([{ texto: 'Resultados' }]);
  const puedeTextos = usePuede('resultados.ver_textos');

  const [aplicacionId, setAplicacionId] = useState<number | null>(null);
  const [area, setArea] = useState<string | null>(null);
  const [cargo, setCargo] = useState<string | null>(null);

  const aplicaciones = useAplicacionesConResultados();
  const cortes = useCortes(aplicacionId);
  const corte = useMemo(
    () => ({ ...(area ? { area } : {}), ...(cargo ? { cargo } : {}) }),
    [area, cargo],
  );
  const informe = useResultados(aplicacionId, corte);

  const opcionesAplicacion = (aplicaciones.data ?? []).map((a) => ({
    clave: a.id,
    texto: `${a.titulo ?? a.cuestionarioNombre} · ${a.empresaNombre} · ${MOMENTO[a.momento]}`,
  }));

  const areas = (cortes.data ?? []).filter((c) => c.clase === 'area');
  const cargos = (cortes.data ?? []).filter((c) => c.clase === 'cargo');

  return (
    <Contenedor>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-marino-800">Resultados</h1>
        <p className="mt-1 text-sm text-marino-500">
          El reparto de respuestas de un cuestionario aplicado. Nunca respuestas de una
          persona.
        </p>
      </div>

      <div className="rounded-[var(--radius-panel)] bg-white p-5 shadow-sm">
        <Combo
          etiqueta="Cuestionario aplicado"
          opciones={opcionesAplicacion}
          valor={aplicacionId}
          alCambiar={(clave) => {
            setAplicacionId(typeof clave === 'number' ? clave : null);
            // Otro instrumento, otros grupos: un área de la anterior puede no existir acá.
            setArea(null);
            setCargo(null);
          }}
          vacio={
            aplicaciones.isLoading
              ? 'Cargando…'
              : opcionesAplicacion.length === 0
                ? 'Todavía no hay cuestionarios aplicados'
                : 'Elige uno para ver sus resultados'
          }
        />

        {aplicacionId !== null && (areas.length > 0 || cargos.length > 0) && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {areas.length > 0 && (
              <Combo
                etiqueta="Área"
                opciones={areas.map((c) => ({ clave: c.valor, texto: c.valor }))}
                valor={area}
                alCambiar={(v) => setArea(typeof v === 'string' ? v : null)}
                vacio="Todas"
              />
            )}
            {cargos.length > 0 && (
              <Combo
                etiqueta="Cargo"
                opciones={cargos.map((c) => ({ clave: c.valor, texto: c.valor }))}
                valor={cargo}
                alCambiar={(v) => setCargo(typeof v === 'string' ? v : null)}
                vacio="Todos"
              />
            )}
          </div>
        )}

        {/*
          ⚠️ El selector NO muestra el conteo de cada grupo, aunque el servidor lo
          manda. Decir «Operaciones (4)» al lado de un grupo cuyo detalle está
          tapado por el mínimo entregaría por la ventana justo lo que el mínimo
          protege. El conteo se usa para saber QUÉ listar, no para mostrarlo.
        */}
      </div>

      {aplicacionId === null ? null : informe.isLoading ? (
        <p className="mt-6 text-sm text-marino-500">Cargando el informe…</p>
      ) : informe.isError ? (
        <p className="mt-6 text-sm font-medium text-red-600">
          No pudimos cargar este informe. Vuelve a intentarlo.
        </p>
      ) : informe.data ? (
        <Informe
          datos={informe.data}
          aplicacionId={aplicacionId}
          puedeTextos={puedeTextos}
          corte={corte}
        />
      ) : null}
    </Contenedor>
  );
}

function Informe({
  datos,
  aplicacionId,
  puedeTextos,
}: {
  datos: import('@copathy/shared').Resultados;
  aplicacionId: number;
  puedeTextos: boolean;
  corte: { area?: string; cargo?: string };
}) {
  /*
    ── «Pocas respuestas» NO es «nadie respondió» ──
    El servidor manda `entregable: false` con `total: null` cuando el grupo no
    alcanza el mínimo de ese cliente. Se dibuja como un aviso que EXPLICA, no como
    un vacío: alguien que ve una pantalla en blanco concluye que su gente no
    participó, y eso es falso y además desalienta.

    El total tampoco se muestra, y no es un olvido: viene en `null` a propósito.
    Decir «hay 4 respuestas» en un área de 5 personas identifica al que falta por
    descarte.
  */
  if (!datos.entregable) {
    return (
      <div className="mt-6 rounded-[var(--radius-panel)] border border-amber-200 bg-amber-50 p-6">
        <h2 className="font-bold text-amber-900">Todavía no se puede entregar este grupo</h2>
        <p className="mt-2 text-sm leading-relaxed text-amber-800">
          Hacen falta al menos <strong>{datos.minimo} respuestas</strong> para mostrar el
          reparto. Con menos, las respuestas de una persona se podrían deducir del resultado.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-amber-800">
          Esto <strong>no significa que nadie haya respondido</strong>. Si elegiste un área o
          un cargo, prueba con el grupo completo.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="rounded-[var(--radius-panel)] bg-white p-5 shadow-sm">
        <h2 className="text-lg font-bold text-marino-800">{datos.titulo}</h2>
        <p className="mt-1 text-sm text-marino-500">
          {datos.empresaNombre} · {datos.cuestionarioNombre} v{datos.version}
          {datos.area ? ` · área ${datos.area}` : ''}
          {datos.cargo ? ` · cargo ${datos.cargo}` : ''}
        </p>
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-marino-50 px-3 py-1.5 text-sm font-semibold text-marino-700">
          <Grafico size={16} />
          {datos.total} {datos.total === 1 ? 'respuesta' : 'respuestas'}
        </p>
      </div>

      {datos.preguntas.map((p) => (
        <Pregunta
          key={p.preguntaId}
          pregunta={p}
          aplicacionId={aplicacionId}
          puedeTextos={puedeTextos}
        />
      ))}
    </div>
  );
}

function Pregunta({
  pregunta,
  aplicacionId,
  puedeTextos,
}: {
  pregunta: PreguntaResultado;
  aplicacionId: number;
  puedeTextos: boolean;
}) {
  const [verTextos, setVerTextos] = useState(false);
  const textos = useTextos(aplicacionId, pregunta.preguntaId, verTextos);

  return (
    <div className="rounded-[var(--radius-panel)] bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold text-marino-800">{pregunta.texto}</h3>
        {pregunta.competencia && (
          <span className="rounded-full bg-naranja-50 px-2.5 py-1 text-xs font-semibold text-naranja-700">
            {pregunta.competencia}
          </span>
        )}
      </div>

      {/* Matriz: una fila por afirmación, cada una con su propio reparto. */}
      {pregunta.filas?.map((f) => (
        <div key={f.itemId} className="mt-4">
          <p className="text-sm font-medium text-marino-600">{f.texto}</p>
          <Barras celdas={f.celdas} />
        </div>
      ))}

      {pregunta.celdas && !pregunta.esTexto && <Barras celdas={pregunta.celdas} />}

      {/*
        ── El texto libre: tras un clic, y solo con permiso ──
        `usePuede` acá SOLO OCULTA el botón; quien valida es `requierePermiso` en
        la API. Y aunque el permiso esté, el servidor puede responder
        `habilitado: false` porque ESE cliente no tiene los textos activados: son
        dos niveles que se multiplican (D-114). Por eso el mensaje distingue los
        dos casos en vez de decir «no hay».
      */}
      {pregunta.esTexto && (
        <div className="mt-4">
          <p className="text-sm text-marino-600">
            {pregunta.celdas?.[0]?.conteo ?? 0} personas escribieron algo.
          </p>
          {puedeTextos &&
            (verTextos ? (
              textos.isLoading ? (
                <p className="mt-2 text-sm text-marino-500">Cargando…</p>
              ) : textos.data?.habilitado === false ? (
                <p className="mt-2 rounded-lg border border-marino-100 bg-marino-50 px-4 py-3 text-sm text-marino-600">
                  Este cliente no tiene habilitada la entrega de respuestas escritas.
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {(textos.data?.respuestas ?? []).map((t, i) => (
                    <li
                      key={i}
                      className="rounded-lg border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm leading-relaxed text-marino-700"
                    >
                      {t}
                    </li>
                  ))}
                  {textos.data?.respuestas.length === 0 && (
                    <li className="text-sm text-marino-500">Nadie escribió nada.</li>
                  )}
                </ul>
              )
            ) : (
              <Boton variante="secundario" onClick={() => setVerTextos(true)} className="mt-2">
                Ver lo que escribieron
              </Boton>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * El reparto, en barras.
 *
 * ── Por qué barras y no un porcentaje solo ──
 * Hoy no se puntúa (D-027): el resultado ES la distribución. Un promedio
 * escondería que «la mitad muy de acuerdo y la mitad muy en desacuerdo» y «todos
 * en el medio» dan el mismo número, y son dos equipos completamente distintos.
 *
 * ⚠️ Se muestra el conteo Y el porcentaje. Solo el porcentaje sobre pocas
 * respuestas engaña: «25 %» de cuatro personas es una, y suena a tendencia.
 */
function Barras({ celdas }: { celdas: Celda[] }) {
  const total = celdas.reduce((s, c) => s + c.conteo, 0);
  if (total === 0) return <p className="mt-2 text-sm text-marino-500">Sin respuestas.</p>;

  return (
    <div className="mt-3 space-y-2">
      {celdas.map((c) => {
        const pct = Math.round((c.conteo / total) * 100);
        return (
          <div key={`${c.valorId ?? c.valorOrden}`} className="flex items-center gap-3">
            <span className="w-40 shrink-0 truncate text-sm text-marino-600" title={c.etiqueta}>
              {c.etiqueta}
            </span>
            <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-marino-100">
              <div
                className="h-full rounded-full bg-naranja-500"
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
            </div>
            <span className="w-24 shrink-0 text-right text-sm font-semibold text-marino-700">
              {c.conteo} · {pct} %
            </span>
          </div>
        );
      })}
    </div>
  );
}
